import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, CONTACT, ASSETS } from "@/data";
import { CTAButton, requestInquiry, scrollToId } from "@/components/site/ui";

const LINES = ["Building futures", "in Canada with", "trusted counsel."];

const MaskLine = ({ children, delay }) => (
  <span className="reveal-mask">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-28 lg:pt-24 pb-16 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-7rem)]">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-6"
          />

          <h1 className="font-heading font-extrabold text-charcoal text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight">
            {LINES.map((line, i) => (
              <MaskLine key={i} delay={0.15 + i * 0.12}>
                {line}
              </MaskLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Professional and personalized immigration guidance for individuals,
            families, and professionals — led by Ali Sleiman, a member in good
            standing with the CICC.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <CTAButton testid="hero-contact-btn" onClick={() => requestInquiry("contact")}>
              Contact Us
            </CTAButton>
            <CTAButton
              testid="hero-book-btn"
              variant="outline"
              onClick={() => requestInquiry("consultation")}
            >
              Book a Consultation
            </CTAButton>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[380px] sm:h-[480px] lg:h-[620px] overflow-hidden"
          >
            <motion.img
              src={IMAGES.parliament}
              alt="Canadian Parliament Hill, Ottawa"
              style={{ y, scale }}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-black/60 to-transparent">
              <p className="text-white/90 text-sm tracking-wide">
                Parliament Hill — Ottawa, Canada&rsquo;s national capital
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <button
        onClick={() => scrollToId("about")}
        data-testid="hero-scroll-cue"
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-charcoal/50 hover:text-crimson transition-colors"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="w-px h-10 bg-current animate-pulse" />
      </button>
    </section>
  );
}
